/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDisaEntryData.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for fetching DISA Entry data.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { fetchDisaEntryById, fetchClassOfServices } from '../api/disaEntriesApi';
import { ClassOfServiceOption, FormState } from '../types';
import { normalizeSettings, generateContextFromName } from '../utils/settingsHelpers';
import { getDefaultFormState } from '../utils/formDefaults';

/**
 * Hook for fetching DISA Entry data
 */
export function useDisaEntryData(id: string | undefined, duplicateId: string | null) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormState>(getDefaultFormState());
  const [classOfServices, setClassOfServices] = useState<ClassOfServiceOption[]>([]);
  const { showError } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const loadClassOfServices = useCallback(async () => {
    try {
      const items = await fetchClassOfServices();
      setClassOfServices(items);
    } catch (error) {
      showErrorRef.current(t('disaEntries:messages.loadClassOfServiceError'));
    }
  }, [t]);

  const loadDisaEntry = useCallback(
    async (entryId: string, duplicate: boolean = false) => {
      setIsLoading(true);
      try {
        const entry = await fetchDisaEntryById(entryId);
        const normalizedSettings = normalizeSettings(entry.settings);

        const loaded: FormState = {
          name: entry.name || '',
          context: entry.context || '',
          password: entry.password || '',
          classOfServicesId: entry.classOfServicesId ?? null,
          enabled: entry.enabled ?? true,
          settings: normalizedSettings
        };

        const copySuffix = t('common.copySuffix', 'Copy');
        const finalData = duplicate
          ? {
              ...loaded,
              name: loaded.name ? `${loaded.name} ${copySuffix}`.trim() : loaded.name,
              context: generateContextFromName(
                loaded.name ? `${loaded.name} ${copySuffix}`.trim() : loaded.name || ''
              )
            }
          : loaded;

        setFormData(finalData);
      } catch (error) {
        showErrorRef.current(t('disaEntries:messages.loadError'));
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    loadClassOfServices();
  }, [loadClassOfServices]);

  useEffect(() => {
    const loadTargetId = duplicateId || id;
    const savedDataKey = 'disaEntries:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (!loadTargetId || loadTargetId === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ? 
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;
      
      const shouldReset = lastLoadedIdRef.current !== 'new' || 
                          lastLocationKeyRef.current !== locationKey ||
                          (lastLoadedIdRef.current === 'new' && loadTargetId === 'new' && 
                           resetTimestamp && resetTimestamp !== lastResetTimestamp);
      
      if (shouldReset) {
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;
        
        // Clear any saved data to ensure fresh form
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }
        
        // Only set default if not in duplicate mode
        if (!duplicateId) {
          setFormData(getDefaultFormState());
        }
      }
      return;
    }

    if (lastLoadedIdRef.current === loadTargetId && lastLocationKeyRef.current === locationKey) {
      return;
    }

    lastLoadedIdRef.current = loadTargetId;
    lastLocationKeyRef.current = locationKey;
    loadDisaEntry(loadTargetId, Boolean(duplicateId)).catch(() => {
      lastLoadedIdRef.current = null;
    });
  }, [duplicateId, id, loadDisaEntry, location.key, location.pathname, location.search]);

  return {
    isLoading,
    formData,
    setFormData,
    classOfServices
  };
}

